/**
 * features/reports/GeneratedReports.tsx
 * ------------------------------------------------------------------
 * FEATURE #4 — class teachers VIEW their own class's reports AFTER
 * generation (read-only). Admin/headteacher/editor see any class.
 * FEATURE #3 — EDITORS can edit the HOY (HOD) and class-teacher
 * comments directly here; saving re-renders & re-uploads the PDF so
 * the stored report reflects the correction.
 *
 * LIVE VIEW — every teaching-staff member (subject teachers included)
 * now reaches this screen for a "Live view": the academic pages only
 * (marks/grades/remarks + a mark-vs-class-average bar), assembled from
 * live marks with NO batch/generation required, scoped to the classes
 * they teach (getAccessibleClasses). The "Final reports" mode is the
 * original batch-gated stored-booklet view (admin/editor/class teacher).
 * ------------------------------------------------------------------
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  setDoc,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

import {
  Eye,
  Save,
  PenLine,
  Lock,
  Loader2,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  FileText,
} from 'lucide-react';
import { db, storage } from '@/lib/firebase';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  getPermissions,
  canViewClassReports,
  getAccessibleClasses,
} from '@/lib/permissions';
import { assembleClassReport } from '@/lib/assembleReport';
import { buildSingleReportPdf, type ReportStudent } from '@/lib/reportPdf';
import LiveReportPreview from '@/features/reports/LiveReportPreview';
import AcademicWebView from '@/features/reports/AcademicWebView';
import { getFormLevel } from '@/lib/grading';
import { getExamTitle, getReportNotice } from '@/lib/assessmentConfig';
import { useGeneratedBatches } from '@/features/reports/useGeneratedBatches';
import GeneratedBatchesList from '@/features/reports/GeneratedBatchesList';
import { resolveTermDates, resolveSchoolReopens } from '@/lib/termDates';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import { sanitizeComment } from '@/lib/sanitize';
import { Card, PageHeader, Spinner, Button } from '@/components/ui/primitives';
import { TermKey, termLabel } from '@/lib/school.config';
import type {
  GeneratedReport,
  ReportBatch,
  ReportCommentsDocument,
  StudentReportComments,
} from '@/types';

export default function GeneratedReports({
  year,
  term,
}: {
  year: string;
  term: TermKey;
}) {
  const { classes, students, subjects, teachers, users, examTitles, reportNotices, termCalendar, loading } = useData();
  const { userData, userRoles } = useAuth();
  const perms = getPermissions(userRoles);
  const canEdit = perms.canEditCommentsOnReport; // editors/admin/head

  // Two views: 'live' = the all-teacher academic pages assembling as marks are
  // entered (no batch, no cover/back); 'final' = the stored generated booklets
  // (batch-gated, admin/editor/class-teacher — the original screen).
  // Default to FINAL (published booklets) once we know the user can view them;
  // the Live view (in-progress academic pages) is the deliberate second tab.
  // Users with no final access stay on Live. initedRef flips the default once.
  const [viewMode, setViewMode] = useState<'live' | 'final'>('live');
  const initedRef = useRef(false);
  const [classId, setClassId] = useState('');
  const [liveStudents, setLiveStudents] = useState<ReportStudent[]>([]);
  const [liveCurrent, setLiveCurrent] = useState(0);
  const [liveLoading, setLiveLoading] = useState(false);
  const [batch, setBatch] = useState<ReportBatch | null>(null);
  const [reports, setReports] = useState<GeneratedReport[]>([]);
  const [current, setCurrent] = useState(0);
  const [mode, setMode] = useState<'carousel' | 'list'>(
    'carousel',
  );
  const [commentsDoc, setCommentsDoc] =
    useState<ReportCommentsDocument | null>(null);
  const [fetching, setFetching] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editCT, setEditCT] = useState('');
  const [editHOD, setEditHOD] = useState('');
  // §3 live preview: the assembled base student (marks/subjects) for the report
  // being edited, plus the logo, loaded once when the editor opens.
  const [previewStudent, setPreviewStudent] = useState<ReportStudent | null>(null);
  const [previewLogo, setPreviewLogo] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // Classes this user may view FINAL (generated) reports for.
  const viewableClasses = useMemo(
    () =>
      classes.filter((c) =>
        !c.archived && canViewClassReports(userData, c.id, classes, teachers, true),
      ),
    [classes, userData, teachers],
  );
  const hasFinalAccess = viewableClasses.length > 0;

  // Open on Final reports by default the first time we learn the user can see
  // them (then leave the choice to the user). No-final-access users stay Live.
  useEffect(() => {
    if (initedRef.current || !hasFinalAccess) return;
    initedRef.current = true;
    setViewMode('final');
  }, [hasFinalAccess]);

  // Generated batches (who/when) the user may view — surfaced as a list so
  // available classes are visible immediately without probing the dropdown.
  const { batches, loading: batchesLoading } = useGeneratedBatches(year, term);
  const viewableBatches = useMemo(
    () => batches.filter((b) => viewableClasses.some((c) => c.id === b.classId)),
    [batches, viewableClasses],
  );

  // Classes this user may open the LIVE academic view for. Privileged staff
  // (admin/head/editor) get every class; teachers get only the classes they
  // class-teach or teach a subject in. `classes` is already canonically sorted.
  const liveClasses = useMemo(
    () =>
      perms.canViewAnyReport
        ? classes
        : getAccessibleClasses(userData, classes, teachers, students),
    [perms.canViewAnyReport, classes, userData, teachers, students],
  );

  const pickList = viewMode === 'live' ? liveClasses : viewableClasses;

  // LIVE view: assemble the whole class from live marks (no batch needed) and
  // page through the academic-only preview per student.
  useEffect(() => {
    if (viewMode !== 'live' || !classId) {
      setLiveStudents([]);
      return;
    }
    let cancelled = false;
    setLiveLoading(true);
    assembleClassReport({
      year,
      term,
      classId,
      classes,
      students,
      subjects,
      teachers,
      users,
      termCalendar,
    })
      .then((a) => {
        if (cancelled) return;
        setLiveStudents(a);
        setLiveCurrent(0);
      })
      .catch(() => !cancelled && setLiveStudents([]))
      .finally(() => !cancelled && setLiveLoading(false));
    return () => {
      cancelled = true;
    };
    // classes/students/subjects/teachers/users/termCalendar are stable arrays
    // from DataContext; re-assemble on class/term/mode change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, classId, year, term, students, subjects, teachers, users, termCalendar]);

  useEffect(() => {
    if (viewMode !== 'final') return;
    if (!classId) {
      setBatch(null);
      setReports([]);
      setCommentsDoc(null);
      return;
    }
    let cancelled = false;
    setFetching(true);
    const batchId = `${year}-${term}-${classId}`;
    Promise.all([
      getDoc(doc(db, COLLECTIONS.reportBatches, batchId)),
      getDocs(
        query(
          collection(db, COLLECTIONS.generatedReports),
          where('year', '==', year),
          where('term', '==', term),
          where('classId', '==', classId),
        ),
      ),
      getDoc(
        doc(db, COLLECTIONS.reportComments, `${year}-${term}-${classId}`),
      ),
    ])
      .then(([b, rs, c]) => {
        if (cancelled) return;
        setBatch(b.exists() ? (b.data() as ReportBatch) : null);
        const list = rs.docs.map((d) => d.data() as GeneratedReport);
        list.sort((a, b2) => a.studentName.localeCompare(b2.studentName));
        setReports(list);
        setCurrent(0);
        setCommentsDoc(
          c.exists() ? (c.data() as ReportCommentsDocument) : null,
        );
      })
      .finally(() => !cancelled && setFetching(false));
    return () => {
      cancelled = true;
    };
  }, [viewMode, classId, year, term]);

  function openEdit(r: GeneratedReport) {
    const e = commentsDoc?.entries?.[r.studentId];
    setEditId(r.studentId);
    setEditCT(e?.classTeacherComment ?? '');
    setEditHOD(e?.hodComment ?? '');
    // Assemble the base report student once so the live preview can re-render
    // the booklet as the editor types (comments are overlaid at render time).
    if (perms.canPreviewReports) {
      setPreviewStudent(null);
      setPreviewLoading(true);
      (async () => {
        try {
          const [assembled, logo] = await Promise.all([
            assembleClassReport({ year, term, classId, classes, students, subjects, teachers, users, termCalendar, commentsDoc }),
            loadLogoDataUrl(),
          ]);
          setPreviewStudent(assembled.find((a) => a.name === r.studentName) ?? null);
          setPreviewLogo(logo);
        } catch {
          setPreviewStudent(null);
        } finally {
          setPreviewLoading(false);
        }
      })();
    }
  }

  async function saveEdit(r: GeneratedReport) {
    setSaving(true);
    setMsg(null);
    try {
      // 1. persist the edited comments (with editor-override meta)
      const cId = `${year}-${term}-${classId}`;
      const existing =
        commentsDoc ??
        ((
          await getDoc(doc(db, COLLECTIONS.reportComments, cId))
        ).data() as ReportCommentsDocument | undefined) ??
        null;
      const entries: Record<string, StudentReportComments> = {
        ...(existing?.entries ?? {}),
      };
      const prev = entries[r.studentId] ?? {
        classTeacherComment: '',
        hodComment: '',
      };
      const now = new Date().toISOString();
      entries[r.studentId] = {
        ...prev,
        classTeacherComment: sanitizeComment(editCT, 800),
        hodComment: sanitizeComment(editHOD, 800),
        classTeacherCommentMeta: {
          ...(prev.classTeacherCommentMeta ?? {}),
          editedByEditor: true,
          editorId: userData?.uid,
          editorName: userData?.displayName,
          editedAt: now,
        },
        hodCommentMeta: {
          ...(prev.hodCommentMeta ?? {}),
          editedByEditor: true,
          editorId: userData?.uid,
          editorName: userData?.displayName,
          editedAt: now,
        },
      };
      const newCommentsDoc: ReportCommentsDocument = {
        id: cId,
        year,
        term,
        classId,
        entries,
      };
      await setDoc(
        doc(db, COLLECTIONS.reportComments, cId),
        { ...newCommentsDoc, updatedAt: now },
        { merge: true },
      );
      setCommentsDoc(newCommentsDoc);

      // 2. re-render this student's PDF with the corrected comments
      //    and overwrite the stored file
      const assembled = await assembleClassReport({
        year,
        term,
        classId,
        classes,
        students,
        subjects,
        teachers,
        users,
        termCalendar,
        commentsDoc: newCommentsDoc,
      });
      const rs = assembled.find((a) => a.name === r.studentName);
      if (rs) {
        const logo = await loadLogoDataUrl();
        const pdf = buildSingleReportPdf(rs, { logoDataUrl: logo, getExamTitle: (fl, t) => getExamTitle(fl, t, examTitles), getReportNotice: (y, t) => getReportNotice(y, t, reportNotices), getTermDates: (tm) => resolveTermDates(tm, termCalendar), getSchoolReopens: (tm) => resolveSchoolReopens(tm, termCalendar) });
        const blob = pdf.output('blob');
        const storageRef = ref(storage, r.storagePath);
        await uploadBytes(storageRef, blob, {
          contentType: 'application/pdf',
        });
        const url = await getDownloadURL(storageRef);
        await setDoc(
          doc(db, COLLECTIONS.generatedReports, r.id),
          {
            downloadUrl: url,
            send: {
              email: 'not_sent',
              whatsapp: 'not_sent',
              lastError:
                'Report edited after generation — re-send if already delivered.',
            },
          },
          { merge: true },
        );
        setReports((list) =>
          list.map((x) =>
            x.id === r.id ? { ...x, downloadUrl: url } : x,
          ),
        );
      }

      setMsg({
        ok: true,
        text: 'Comments corrected and the stored report was regenerated.',
      });
      setEditId(null);
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <Spinner label="Loading…" />;

  return (
    <div>
      <PageHeader
        title={
          viewMode === 'live'
            ? 'View Reports'
            : canEdit
              ? 'Review & Edit Reports'
              : 'My Class Reports'
        }
        subtitle={
          viewMode === 'live'
            ? `${termLabel(term)} ${year} · academic pages, updating as marks are entered`
            : canEdit
              ? `${termLabel(term)} ${year} · editors may correct HOY & class-teacher comments here`
              : `${termLabel(term)} ${year} · view your class's generated reports`
        }
      />

      {hasFinalAccess && (
        <div className="inline-flex items-center gap-1 mb-4 p-1 rounded-xl bg-gray-100 dark:bg-gray-800 text-sm">
          <button
            onClick={() => {
              setViewMode('final');
              setClassId('');
            }}
            className={`px-3 py-1.5 rounded-lg font-medium ${
              viewMode === 'final'
                ? 'seg-on'
                : 'seg-off'
            }`}
          >
            Final reports
          </button>
          <button
            onClick={() => {
              setViewMode('live');
              setClassId('');
            }}
            className={`px-3 py-1.5 rounded-lg font-medium ${
              viewMode === 'live'
                ? 'seg-on'
                : 'seg-off'
            }`}
          >
            Live view · in progress
          </button>
        </div>
      )}

      {msg && (
        <div
          className={`mb-4 text-sm rounded-xl px-3 py-2.5 border ${
            msg.ok
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
              : 'bg-red-50 text-red-700 border-red-200'
          }`}
        >
          {msg.text}
        </div>
      )}

      <Card className="p-5 mb-4">
        <label className="text-sm font-medium">Class</label>
        <select
          value={classId}
          onChange={(e) => setClassId(e.target.value)}
          className="w-full sm:w-72 mt-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800"
        >
          <option value="">— select class —</option>
          {pickList.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        {pickList.length === 0 && (
          <p className="text-xs text-gray-500 mt-2">
            {viewMode === 'live'
              ? 'No classes assigned to you yet.'
              : 'No classes available — you can view reports only for your own class once they have been generated.'}
          </p>
        )}
      </Card>

      {/* Available generated classes (Final mode) — click a row to open it. */}
      {viewMode === 'final' && (
        <div className="mb-4">
          <GeneratedBatchesList
            batches={viewableBatches}
            loading={batchesLoading}
            title="Available to view"
            emptyText="No generated reports available to you for this term yet."
            highlightId={classId ? `${year}-${term}-${classId}` : undefined}
            onRowClick={(b) => setClassId(b.classId)}
          />
        </div>
      )}

      {/* ---------- LIVE academic view ---------- */}
      {viewMode === 'live' && classId && liveLoading && (
        <Spinner label="Assembling academic pages…" />
      )}

      {viewMode === 'live' && classId && !liveLoading && liveStudents.length === 0 && (
        <Card className="p-12 text-center text-sm text-gray-500">
          <FileText size={20} className="mx-auto mb-2 text-gray-400" />
          No students enrolled in this class yet.
        </Card>
      )}

      {viewMode === 'live' && classId && !liveLoading && liveStudents.length > 0 && (() => {
        const s = liveStudents[liveCurrent];
        if (!s) return null;
        return (
          <Card className="overflow-hidden">
            <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3">
              <button
                onClick={() => setLiveCurrent((i) => Math.max(0, i - 1))}
                disabled={liveCurrent === 0}
                className="flex items-center gap-1 px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-sm font-medium disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                <ChevronLeft size={16} /> Prev
              </button>
              <div className="text-center min-w-0">
                <p className="font-bold text-gray-800 dark:text-white truncate">
                  {s.name}
                </p>
                <p className="text-xs text-gray-500">
                  {liveCurrent + 1} of {liveStudents.length} · {s.className}
                </p>
              </div>
              <button
                onClick={() =>
                  setLiveCurrent((i) => Math.min(liveStudents.length - 1, i + 1))
                }
                disabled={liveCurrent === liveStudents.length - 1}
                className="flex items-center gap-1 px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-sm font-medium disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Next <ChevronRight size={16} />
              </button>
            </div>
            <div className="p-4">
              <AcademicWebView
                key={s.name}
                student={s}
                examTitle={getExamTitle(getFormLevel(s.className), term, examTitles)}
              />
              {/* student strip — click to jump */}
              <div className="flex gap-1.5 overflow-x-auto mt-4 pb-1">
                {liveStudents.map((rep, i) => (
                  <button
                    key={rep.name}
                    onClick={() => setLiveCurrent(i)}
                    className={`px-3 py-1.5 rounded-lg text-xs whitespace-nowrap transition-colors ${
                      i === liveCurrent
                        ? 'bg-primary text-white font-semibold'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                  >
                    {rep.name}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-gray-400 mt-3">
                Live academic pages — marks and grades update as teachers enter
                them. The cover and back page appear only on the final generated
                report.
              </p>
            </div>
          </Card>
        );
      })()}

      {/* ---------- FINAL (generated) reports ---------- */}
      {viewMode === 'final' && classId && fetching && (
        <Spinner label="Loading reports…" />
      )}

      {viewMode === 'final' && classId && !fetching && !batch && (
        <Card className="p-12 text-center text-sm text-gray-500">
          <Lock size={20} className="mx-auto mb-2 text-gray-400" />
          Reports for this class have not been generated yet. They become
          viewable once an administrator generates them.
        </Card>
      )}

      {viewMode === 'final' && classId && !fetching && batch && reports.length > 0 && (
        <Card className="overflow-hidden">
          <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3">
            <span className="text-xs text-gray-500">
              Generated{' '}
              {new Date(batch.generatedAt).toLocaleString()} ·{' '}
              {reports.length} reports
            </span>
            <div className="flex items-center gap-1 text-xs">
              <button
                onClick={() => setMode('carousel')}
                className={`px-2.5 py-1.5 rounded-lg font-medium flex items-center gap-1 ${
                  mode === 'carousel'
                    ? 'bg-primary text-white'
                    : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                <Eye size={13} /> Preview
              </button>
              <button
                onClick={() => setMode('list')}
                className={`px-2.5 py-1.5 rounded-lg font-medium flex items-center gap-1 ${
                  mode === 'list'
                    ? 'bg-primary text-white'
                    : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                <LayoutGrid size={13} /> List
              </button>
            </div>
          </div>

          {mode === 'carousel' ? (
            <div className="p-4">
              {(() => {
                const r = reports[current];
                if (!r) return null;
                return (
                  <>
                    <div className="flex items-center justify-between gap-3 mb-4">
                      <button
                        onClick={() => setCurrent((i) => Math.max(0, i - 1))}
                        disabled={current === 0}
                        aria-label="Previous"
                        className="w-10 h-10 shrink-0 rounded-full border border-gray-200 dark:border-gray-700 flex items-center justify-center disabled:opacity-30 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                      >
                        <ChevronLeft size={18} />
                      </button>
                      <div className="text-center min-w-0">
                        <p className="text-lg font-bold text-gray-900 dark:text-white truncate">
                          {r.studentName}
                        </p>
                        <span className="inline-flex items-center gap-1.5 mt-1 px-2.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-xs text-gray-500 dark:text-gray-400">
                          {r.className} · {current + 1} of {reports.length}
                        </span>
                      </div>
                      <button
                        onClick={() => setCurrent((i) => Math.min(reports.length - 1, i + 1))}
                        disabled={current === reports.length - 1}
                        aria-label="Next"
                        className="w-10 h-10 shrink-0 rounded-full border border-gray-200 dark:border-gray-700 flex items-center justify-center disabled:opacity-30 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                      >
                        <ChevronRight size={18} />
                      </button>
                    </div>

                    {r.downloadUrl ? (
                      <div className="rounded-2xl overflow-hidden ring-1 ring-black/5 dark:ring-white/10 shadow-sm bg-gray-100 dark:bg-gray-900">
                        <iframe
                          key={r.id}
                          src={`${r.downloadUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                          title={`Report — ${r.studentName}`}
                          className="w-full"
                          style={{ height: '78vh' }}
                        />
                      </div>
                    ) : (
                      <div className="rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 p-12 text-center text-sm text-gray-500">
                        This report has no rendered PDF yet.
                      </div>
                    )}

                    {canEdit && (
                      <div className="flex justify-end mt-3">
                        <Button
                          variant="ghost"
                          icon={<PenLine size={14} />}
                          onClick={() => openEdit(r)}
                        >
                          Edit comments
                        </Button>
                      </div>
                    )}

                    {/* student strip — click to jump */}
                    <div className="flex gap-1.5 overflow-x-auto mt-4 pb-1">
                      {reports.map((rep, i) => (
                        <button
                          key={rep.id}
                          onClick={() => setCurrent(i)}
                          className={`px-3 py-1.5 rounded-lg text-xs whitespace-nowrap transition-colors ${
                            i === current
                              ? 'bg-primary text-white font-semibold'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                          }`}
                        >
                          {rep.studentName}
                        </button>
                      ))}
                    </div>

                  </>
                );
              })()}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/50 text-left text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="px-3 py-2.5 font-medium">
                    Student
                  </th>
                  <th className="px-3 py-2.5 font-medium">
                    Report
                  </th>
                  {canEdit && (
                    <th className="px-3 py-2.5 font-medium text-right">
                      Edit comments
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {reports.map((r, i) => (
                  <tr
                    key={r.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800/40"
                  >
                    <td className="px-3 py-2 font-medium">
                      {r.studentName}
                    </td>
                    <td className="px-3 py-2">
                      {r.downloadUrl ? (
                        <button
                          onClick={() => {
                            setCurrent(i);
                            setMode('carousel');
                          }}
                          className="inline-flex items-center gap-1 text-primary hover:underline"
                        >
                          <Eye size={14} /> Preview
                        </button>
                      ) : (
                        <span className="text-gray-400">
                          —
                        </span>
                      )}
                    </td>
                    {canEdit && (
                      <td className="px-3 py-2 text-right">
                        <Button
                          variant="ghost"
                          icon={<PenLine size={14} />}
                          onClick={() => openEdit(r)}
                        >
                          Edit
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {/* Editor edit modal (feature #3) */}
      {editId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm overflow-y-auto">
          <Card glass className={`w-full ${perms.canPreviewReports ? 'max-w-5xl' : 'max-w-lg'} p-6 animate-fade-in my-8`}>
            <h2 className="text-lg font-bold mb-1">
              Correct comments
            </h2>
            <p className="text-xs text-gray-500 mb-4">
              {reports.find((r) => r.studentId === editId)?.studentName}.
              Saving regenerates and replaces the stored PDF.
            </p>
            <div className={perms.canPreviewReports ? 'grid md:grid-cols-2 gap-6' : ''}>
              <div>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">
                      Class Teacher — Attendance &amp; Conduct
                    </label>
                    <textarea
                      rows={4}
                      value={editCT}
                      onChange={(e) => setEditCT(e.target.value)}
                      className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-900 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">
                      Head of Year — on behalf of the Principal
                    </label>
                    <textarea
                      rows={4}
                      value={editHOD}
                      onChange={(e) => setEditHOD(e.target.value)}
                      className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-900 text-sm"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-6">
                  <Button
                    variant="ghost"
                    onClick={() => setEditId(null)}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    icon={
                      saving ? (
                        <Loader2 size={15} className="animate-spin" />
                      ) : (
                        <Save size={15} />
                      )
                    }
                    disabled={saving}
                    onClick={() => {
                      const r = reports.find(
                        (x) => x.studentId === editId,
                      );
                      if (r) saveEdit(r);
                    }}
                  >
                    Save &amp; regenerate
                  </Button>
                </div>
              </div>

              {perms.canPreviewReports && (
                <div className="hidden md:flex flex-col">
                  <label className="text-xs font-medium text-gray-500 mb-1">
                    Live preview <span className="text-gray-400">— updates as you type</span>
                  </label>
                  {previewLoading || !previewStudent ? (
                    <div className="flex-1 min-h-[65vh] rounded-lg bg-gray-100 dark:bg-gray-900 flex items-center justify-center text-sm text-gray-400">
                      <Loader2 size={15} className="animate-spin mr-2" /> Loading preview…
                    </div>
                  ) : (
                    <LiveReportPreview
                      student={{ ...previewStudent, classTeacherComment: editCT, hodComment: editHOD }}
                      options={{
                        logoDataUrl: previewLogo,
                        getExamTitle: (fl, t) => getExamTitle(fl, t, examTitles),
                        getReportNotice: (y, t) => getReportNotice(y, t, reportNotices),
                        getTermDates: (tm) => resolveTermDates(tm, termCalendar),
                        getSchoolReopens: (tm) => resolveSchoolReopens(tm, termCalendar),
                      }}
                      className="flex-1 min-h-[65vh]"
                    />
                  )}
                </div>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
